import {
    AirVent,
    ArrowUpRight,
    BadgeCheck,
    ClipboardList,
    DoorClosed,
    FlaskConical,
    Gauge,
    GraduationCap,
    Grip,
    LayoutGrid,
    Layers,
    Package,
    Route,
    ShieldCheck,
    Table2,
    Users,
    Wind,
} from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { scrollToId } from "../lib";

const IMG = {
    modularOt:
        "https://images.unsplash.com/photo-1770836037423-d48c88ec60b9?crop=entropy&cs=srgb&fm=jpg&q=85",
    cssd: "https://images.pexels.com/photos/8940466/pexels-photo-8940466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const OT_SCOPE = [
    { t: "Vinyl / SS / PPGI Wall Panelling", icon: Layers },
    { t: "Calcium Silicate Ceiling", icon: LayoutGrid },
    { t: "Conductive Flooring", icon: Grip },
    { t: "Hermetically Sealing OT Door", icon: DoorClosed },
    {
        t: "Laminar Air Flow & AHU Design / Implementation with Control Panel",
        icon: AirVent,
    },
    {
        t: "Surgical / Anaesthetic Pendant",
        img: "/products/medical-gas-pendant-cut.png",
    },
    { t: "OT Table / Light", img: "/products/c-arm-ot-table-cut.png" },
    { t: "NABH Validation of OT", icon: BadgeCheck },
    { t: "Negative / Positive Pressure Implementation", icon: Gauge },
    { t: "Training Hospital Staff on Infection Control", icon: GraduationCap },
    {
        t: "OT Cleaning Techniques & Infrastructure",
        img: "/products/scrub-station-2-bay-4-operations-cut.png",
    },
];

const CSSD_SCOPE = [
    { t: "CSSD Layout & Workflow Design — Dirty → Clean → Sterile Zoning", icon: Route },
    { t: "SS Worktables, Washing & Packing Stations", icon: Table2 },
    {
        t: "Washer Disinfector & Ultrasonic Cleaning Station",
        img: "/products/ultrasonic-cleaner-cut.png",
    },
    {
        t: "Horizontal Steam & ETO Sterilizer Installation",
        img: "/products/horizontal-sterilizer-cut.png",
    },
    { t: "Sterile Store SS Racking & Dispatch", icon: Package },
    { t: "Air Handling & Pressure Cascade Design", icon: Wind },
    { t: "Staff Training on Sterilization SOPs", icon: Users },
    { t: "Cycle Documentation & Record Systems", icon: ClipboardList },
    { t: "Monitoring with Chemical & Biological Indicators", icon: FlaskConical },
    { t: "NABH-Aligned CSSD Validation Support", icon: ShieldCheck },
];

const enquire = (interest, product) => {
    window.dispatchEvent(
        new CustomEvent("ss:enquire-product", {
            detail: { interest, product },
        }),
    );
    scrollToId("contact");
};

function Panel({ banner, title, sub, items, testid, interest }) {
    return (
        <Reveal y={32}>
            <div
                data-testid={testid}
                className="overflow-hidden rounded-3xl border border-line bg-panel/50"
            >
                <div className="relative h-44 overflow-hidden sm:h-52">
                    <img
                        src={banner}
                        alt={title}
                        loading="lazy"
                        className="spot-img h-full w-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-panel via-panel/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-7">
                        <h3 className="font-display text-xl font-bold text-white sm:text-2xl">
                            {title}
                        </h3>
                        <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
                            {sub}
                        </p>
                    </div>
                </div>
                <ul className="px-6 py-4 sm:px-7">
                    {items.map((item, i) => (
                        <li
                            key={i}
                            data-testid={`${testid}-item-${i}`}
                            onClick={() =>
                                enquire(interest, `${item.t} — ${title} project`)
                            }
                            title="Enquire about this"
                            className="group flex cursor-pointer items-center gap-4 border-b border-line py-3.5 last:border-b-0"
                        >
                            <span className="product-tile flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line transition-colors duration-300 group-hover:border-accent/40">
                                {item.img ? (
                                    <img
                                        src={item.img}
                                        alt={item.t}
                                        loading="lazy"
                                        className="h-11 w-11 object-contain"
                                    />
                                ) : (
                                    <item.icon className="h-5 w-5 text-accent" />
                                )}
                            </span>
                            <span className="text-sm leading-snug text-slate-200 transition-colors duration-300 group-hover:text-white">
                                {item.t}
                            </span>
                            <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-slate-600 opacity-0 transition-all duration-300 group-hover:text-accent group-hover:opacity-100" />
                        </li>
                    ))}
                </ul>
                <div className="px-6 pb-6 sm:px-7">
                    <button
                        data-testid={`${testid}-enquire-button`}
                        onClick={() =>
                            enquire(interest, `${title} — turnkey project`)
                        }
                        className="group flex w-full items-center justify-center gap-2 rounded-full border border-accent/50 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-accent transition-all duration-300 hover:bg-accent hover:text-ink"
                    >
                        Enquire about {title}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                    </button>
                </div>
            </div>
        </Reveal>
    );
}

export default function Projects() {
    return (
        <section
            id="projects"
            data-testid="projects-section"
            className="relative overflow-hidden py-24 lg:py-32"
        >
            <div className="pointer-events-none absolute left-[-10%] top-1/4 h-[460px] w-[460px] rounded-full bg-accent/5 blur-[150px]" />
            <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <SectionHead
                        label="Where We Innovate"
                        title="From bare shell to validated theatre."
                    />
                    <Reveal delay={0.15}>
                        <p className="max-w-sm text-sm leading-relaxed text-slate-400">
                            Every layer of a clean room &amp; modular OT — and
                            a complete CSSD — designed, built, validated and
                            handed over with training.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-14 grid items-start gap-6 lg:grid-cols-2">
                    <Panel
                        testid="projects-ot-panel"
                        banner={IMG.modularOt}
                        title="Cleanroom & Modular OT"
                        sub="Design & Development"
                        items={OT_SCOPE}
                        interest="Cleanroom & Modular OT (Turnkey Project)"
                    />
                    <Panel
                        testid="projects-cssd-panel"
                        banner={IMG.cssd}
                        title="CSSD"
                        sub="Design · Manufacture · Training · Monitoring"
                        items={CSSD_SCOPE}
                        interest="CSSD (Design & Build)"
                    />
                </div>

                <Reveal delay={0.15}>
                    <div className="mt-12 flex flex-wrap items-center justify-between gap-5 rounded-2xl border border-line bg-panel/50 px-7 py-6">
                        <p className="font-display text-lg font-bold text-white sm:text-xl">
                            Planning a theatre or CSSD of your own?
                        </p>
                        <button
                            data-testid="projects-enquire-button"
                            onClick={() => {
                                enquire(
                                    "Cleanroom & Modular OT (Turnkey Project)",
                                    "Cleanroom & Modular OT / CSSD project",
                                );
                                window.dispatchEvent(
                                    new CustomEvent("ss:enquire-product", {
                                        detail: {
                                            interest: "CSSD (Design & Build)",
                                        },
                                    }),
                                );
                            }}
                            className="group inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:scale-[1.04]"
                        >
                            Start the conversation
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                        </button>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
